import React, { useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useDelayedCallback } from '@vmos2/utils';
import { useTheme } from '@emotion/react';
import { Icon } from '..';

type Props = {
  type: 'success' | 'danger' | 'info' | 'warning',
  id: string,
  message: string,
  clickHandler: () => void,
  removeToast: (id: string) => void,
  additional?: String,
  timeout?: number,
  accessibleText?: string,
};

const iconMap = {
  success: 'CheckCircle',
  danger: 'CrossCircle',
  info: 'InfoCircle',
  warning: 'WarningCircle',
};

export const Toast = ({ type, id, message, additional, timeout, clickHandler, removeToast, accessibleText }: Props) => {
  const theme = useTheme();
  const [hasAnimation, onRemoveProductWithAnimation] = useDelayedCallback(() => removeToast(id), 1000);

  const closeHandler = useCallback(
    e => {
      e.stopPropagation();
      onRemoveProductWithAnimation();
    },
    [id]
  );


  useEffect(() => {
    const timeOut = setTimeout(() => onRemoveProductWithAnimation(), timeout || 2000);
    return () => {
      clearTimeout(timeOut);
    };
  }, [id]);

  return (
    <ToastWrapper key={id} onClick={clickHandler} hasAnimation={hasAnimation}>
      <LeftBar type={type} />
      <ToastIcon name={iconMap[type]} fill={theme.color[type]} />
      <ToastMessage type={type}>{message}</ToastMessage>
      <ToastRight>
        {additional && <ToastAdditional>{additional}</ToastAdditional>}
        <CloseIcon name="Plus" fill={theme.color.grey} onClick={closeHandler} />
      </ToastRight>
      {accessibleText && <span className="hide-text">{accessibleText}</span>}
    </ToastWrapper>
  );
};

const ToastWrapper = styled('div')`
  --margin: 16px;
  --minHeight: 48px;
  position: relative;
  display: flex;
  text-align: center;
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.boxShadow.medium};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  padding: 16px;
  transition: ${({ theme }) => theme.motion.transition};
  animation: toast-bottom 0.7s;
  margin: var(--margin) auto;
  width: 90%;
  min-height: var(--minHeight);
  max-width: 400px;
  font-size: ${({ theme }) => theme.fontSize.md};
  z-index: ${({ theme }) => theme.positioning.modalOverlay};
  overflow: hidden;

  @keyframes toast-bottom {
    from {
      transform: translateX(100%);
    }

    to {
      transform: translateX(0);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      margin-top: 0;
    }

    to {
      opacity: 0;
      margin-top: calc((var(--minHeight) + var(--margin)) * -1);
    }
  }

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    margin: 0 16px 25px 0;
    width: 400px;
    min-height: 52px;
    font-size: ${({ theme }) => theme.fontSize.lg};
  }

  ${({ hasAnimation }) => hasAnimation && 'animation: fadeOut 0.7s forwards'};
`;

const LeftBar = styled('div')`
  position: absolute;
  width: 4px;
  left: 0;
  top: 0;
  bottom: 0;
  background: ${({ theme, type }) => theme.color[type]};
`;

const CloseIcon = styled(Icon)`
  min-width: 16px;
  margin-left: 8px;
  transform: rotate(45deg);
  cursor: pointer;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    min-width: 18px;
  }
`;

const ToastIcon = styled(Icon)`
  min-width: 16px;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    min-width: 18px;
  }
`;

const ToastMessage = styled('span')`
  margin-left: 16px;
  word-break: break-word;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ type }) => type === 'danger' && 'bold'};
  color: ${({ theme, type }) => type === 'danger' && theme.color[type]};
  text-align: left;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;

const ToastAdditional = styled('span')`
  font-size: ${({ theme }) => theme.fontSize.sm};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;

const ToastRight = styled('span')`
  margin-left: auto;
  display: flex;
`;
